import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Lock } from 'lucide-react';
import { getNotasInternas, setNotasInternas } from '../../lib/citasApi';
import { formatDateTime } from '../../lib/api';
import { SectionCard } from '../Common';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

/**
 * Notas internas del paciente (pagos realizados, gestiones, recordatorios…).
 *
 * SOLO administración y dirección (admin/direccion/administrativo): ni enfermería ni
 * médicos — el backend lo exige y la pestaña ni se monta para otros roles
 * (gate en PatientDetailPage con canSeeInternalNotes).
 */
export default function NotasInternasTab({ patient }) {
  const [texto, setTexto] = useState('');
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // No se ha podido LEER lo que hay guardado. Es distinto de «no hay nada», y
  // la diferencia es un dato que se pierde para siempre (revisión 2026-09-19):
  // un parpadeo de red pintaba el recuadro VACÍO con «Sin notas todavía» sobre
  // una ficha que dice «Debe 300 €» o «No dar cita hasta que pague»; en cuanto
  // alguien tecleaba una línea y guardaba, el PUT reemplaza el texto ENTERO y
  // esas dos líneas desaparecían de la base. Guardar encima de lo que no se ha
  // podido leer es pisarlo.
  const [fallo, setFallo] = useState(false);
  const loadedFor = useRef(null);
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    if (!patient?.id || loadedFor.current === patient.id) return;
    loadedFor.current = patient.id;
    setLoading(true);
    setFallo(false);
    getNotasInternas(patient.id)
      .then((d) => {
        setTexto(d?.texto || '');
        setMeta(d?.updated_at ? d : null);
        setDirty(false);
      })
      .catch((e) => {
        setFallo(true);
        loadedFor.current = null;   // para que «Reintentar» vuelva a pedirlas
        toast.error(e?.response?.data?.detail || 'No se pudieron cargar las notas internas');
      })
      .finally(() => setLoading(false));
  }, [patient?.id, recarga]);

  const save = async () => {
    setSaving(true);
    try {
      const d = await setNotasInternas(patient.id, texto);
      setMeta(d);
      setDirty(false);
      toast.success('Notas internas guardadas');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudieron guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-2">
          <Lock className="w-4 h-4 text-[#13231E]" /> Notas de administración
        </span>
      }
    >
      <p className="text-xs text-muted-foreground mb-3">
        Visibles solo para administración y dirección (ni enfermería ni médicos).
        Pagos realizados, gestiones pendientes, recordatorios…
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      ) : fallo ? (
        <div className="py-6 text-sm" data-testid="notas-internas-fallo">
          <p className="text-[#8A1C1C] font-medium">
            No se han podido consultar las notas de administración.
          </p>
          <p className="text-muted-foreground mt-1">
            Esto NO quiere decir que no tenga. No se escribe encima de lo que no
            se ha podido leer: vuelve a intentarlo antes de escribir nada.
          </p>
          <Button
            type="button"
            variant="outline"
            className="mt-3"
            onClick={() => { loadedFor.current = null; setFallo(false); setRecarga((n) => n + 1); }}
            data-testid="notas-internas-reintentar"
          >
            Reintentar
          </Button>
        </div>
      ) : (
        <>
          <Textarea
            value={texto}
            onChange={(e) => { setTexto(e.target.value); setDirty(true); }}
            placeholder={'Ej.:\n12/07 — Pago recibido (Bizum)\nPendiente enviar presupuesto revisado'}
            rows={10}
            className="font-mono text-sm"
            data-testid="notas-internas-texto"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="text-[11px] text-muted-foreground">
              {meta?.updated_at
                ? `Última edición: ${formatDateTime(meta.updated_at)}${meta.updated_by_name ? ` · ${meta.updated_by_name}` : ''}`
                : 'Sin notas todavía'}
            </span>
            <Button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="notas-internas-guardar"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </div>
        </>
      )}
    </SectionCard>
  );
}
